// src/components/admin/SubIndustryDetailsPage.tsx - Fully Editable Sub-Industry Detail Page
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Divider,
  Container,
  Breadcrumbs,
  Link,
  useTheme,
  alpha,
  Snackbar,
  Alert,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ListItemIcon,
  Tooltip,
  Fab,
  CircularProgress
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Dashboard as DashboardIcon,
  Gavel as GavelIcon,
  Lightbulb as LightbulbIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  MonetizationOn as MonetizationOnIcon,
  Check as CheckIcon,
  Category as CategoryIcon,
  TextFields as TextFieldsIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import * as industryApi from '../../services/industry';
import type { Industry, SubIndustry } from '../../services/industry';

// Import the editable components
import EditableValueChainTable from '../valueChain/EditableValueChainTable';
import { Edit } from 'lucide-react';

const MotionPaper = motion(Paper);

// Helper TypeScript interfaces
interface KeyMetrics {
  employment: string;
  exports: string;
  companies: number;
}

interface GrowthDataPoint {
  year: string;
  value: number;
}

interface TabContent {
  marketOverview?: any;
  legalInfrastructure?: any;
  trends?: any;
  incentives?: any;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

// Editable Text Component (same as in IndustryDetailsPage)
const EditableText: React.FC<{
  value: string;
  onSave: (value: string) => void;
  variant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'body1' | 'body2' | 'subtitle1' | 'subtitle2';
  multiline?: boolean;
  rows?: number;
  placeholder?: string;
  fontWeight?: number;
  color?: string;
  sx?: any;
}> = ({ 
  value, 
  onSave, 
  variant = 'body1', 
  multiline = false, 
  rows = 1, 
  placeholder = 'Click to edit...',
  fontWeight,
  color,
  sx = {}
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const theme = useTheme();

  const handleSave = () => {
    onSave(editValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <Box sx={{ position: 'relative', ...sx }}>
        <TextField
          fullWidth
          multiline={multiline}
          rows={rows}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          placeholder={placeholder}
          variant="outlined"
          size="small"
          onBlur={handleSave}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !multiline) {
              handleSave();
            }
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
              '& fieldset': {
                borderColor: theme.palette.primary.main,
              }
            }
          }}
          InputProps={{
            endAdornment: (
              <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                <IconButton size="small" onClick={handleSave} color="primary">
                  <CheckIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={handleCancel}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )
          }}
          autoFocus
        />
      </Box>
    );
  }

  return (
    <Box 
      sx={{
        position: 'relative',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.05),
          '& .edit-indicator': {
            opacity: 1
          }
        },
        p: 0.5,
        borderRadius: 1,
        ...sx
      }}
      onClick={() => setIsEditing(true)}
    >
      <Typography 
        variant={variant} 
        sx={{ 
          fontWeight, 
          color, 
          minHeight: '1em',
          ...(value ? {} : { 
            color: theme.palette.text.disabled,
            fontStyle: 'italic'
          })
        }}
      >
        {value || placeholder}
      </Typography>
      <TextFieldsIcon 
        className="edit-indicator"
        sx={{
          position: 'absolute',
          top: 2,
          right: 2,
          fontSize: 12,
          opacity: 0,
          transition: 'opacity 0.2s',
          color: theme.palette.primary.main
        }}
      />
    </Box>
  );
};

// Editable List Component
const EditableList: React.FC<{
  items: string[];
  onUpdate: (items: string[]) => void;
  title?: string;
  allowAdd?: boolean;
  allowDelete?: boolean;
}> = ({ items, onUpdate, title, allowAdd = true, allowDelete = true }) => {
  const theme = useTheme();
  
  const handleItemChange = (index: number, value: string) => {
    const newItems = [...items];
    newItems[index] = value;
    onUpdate(newItems);
  };

  const handleAddItem = () => {
    onUpdate([...items, '']);
  };

  const handleDeleteItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    onUpdate(newItems);
  };

  return (
    <Box sx={{ mt: 2 }}>
      {title && (
        <Typography variant="h6" fontWeight={600} color="primary.main" gutterBottom>
          {title}
        </Typography>
      )}
      <List sx={{ mt: 1 }}>
        {items.map((item, index) => (
          <ListItem key={index} sx={{ py: 1, px: 0 }}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <CheckIcon color="primary" fontSize="small" />
            </ListItemIcon>
            <EditableText
              value={item}
              onSave={(value) => handleItemChange(index, value)}
              placeholder="Enter item..."
              sx={{ flex: 1 }}
            />
            {allowDelete && (
              <ListItemSecondaryAction>
                <IconButton
                  size="small"
                  onClick={() => handleDeleteItem(index)}
                  sx={{ color: theme.palette.error.main }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </ListItemSecondaryAction>
            )}
          </ListItem>
        ))}
      </List>
      {allowAdd && (
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddItem}
          sx={{ mt: 1 }}
          variant="outlined"
        >
          Add Item
        </Button>
      )}
    </Box>
  );
};

// Editable Object Component (same implementation as in the fixed IndustryDetailsPage)
const EditableObject: React.FC<{
  data: any;
  onUpdate: (data: any) => void;
  title?: string;
}> = ({ data, onUpdate, title }) => {
  const theme = useTheme();

  const handleFieldChange = (field: string, value: any) => {
    onUpdate({
      ...data,
      [field]: value
    });
  };

  const handleNestedFieldChange = (parentKey: string, childKey: string, value: any) => {
    const updatedParent = {
      ...data[parentKey],
      [childKey]: value
    };
    handleFieldChange(parentKey, updatedParent);
  };

  const renderComplexField = (key: string, value: any, depth = 0, parentPath = '') => {
    const currentPath = parentPath ? `${parentPath}.${key}` : key;
    const indent = depth * 20;

    // Handle arrays of strings
    if (Array.isArray(value) && value.every(item => typeof item === 'string')) {
      return (
        <Box key={currentPath} sx={{ ml: `${indent}px`, mb: 2 }}>
          <EditableList
            items={value}
            onUpdate={(newItems) => {
              if (parentPath) {
                const pathParts = parentPath.split('.');
                if (pathParts.length === 1) {
                  handleNestedFieldChange(pathParts[0], key, newItems);
                } else {
                  handleFieldChange(pathParts[0], {
                    ...data[pathParts[0]],
                    [key]: newItems
                  });
                }
              } else {
                handleFieldChange(key, newItems);
              }
            }}
            title={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
          />
        </Box>
      );
    }

    // Handle arrays of objects (like incentives sections)
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
      return (
        <Box key={currentPath} sx={{ ml: `${indent}px`, mb: 3 }}>
          <Typography variant="h6" fontWeight={600} color="primary.main" gutterBottom>
            {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
          </Typography>
          {value.map((item, index) => (
            <Paper key={index} elevation={1} sx={{ p: 2, mb: 2, backgroundColor: alpha(theme.palette.background.default, 0.3) }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Item {index + 1}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => {
                    const newArray = value.filter((_, i) => i !== index);
                    if (parentPath) {
                      handleNestedFieldChange(parentPath, key, newArray);
                    } else {
                      handleFieldChange(key, newArray);
                    }
                  }}
                  sx={{ color: theme.palette.error.main }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
              {Object.entries(item).map(([itemKey, itemValue]) => 
                renderComplexField(itemKey, itemValue, depth + 1, currentPath + `[${index}]`)
              )}
            </Paper>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              const newItem = value.length > 0 ? 
                Object.keys(value[0]).reduce((acc, k) => {
                  acc[k] = Array.isArray(value[0][k]) ? [] : '';
                  return acc;
                }, {} as any) : 
                { title: '', points: [] };
              
              const newArray = [...value, newItem];
              if (parentPath) {
                handleNestedFieldChange(parentPath, key, newArray);
              } else {
                handleFieldChange(key, newArray);
              }
            }}
            variant="outlined"
            sx={{ mt: 1 }}
          >
            Add {key.slice(0, -1)} Item
          </Button>
        </Box>
      );
    }

    // Handle simple objects
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      return (
        <Box key={currentPath} sx={{ ml: `${indent}px`, mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} color="primary.main" gutterBottom>
            {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
          </Typography>
          <Paper elevation={1} sx={{ p: 2, backgroundColor: alpha(theme.palette.background.default, 0.5) }}>
            {Object.entries(value).map(([subKey, subValue]) => 
              renderComplexField(subKey, subValue, depth + 1, currentPath)
            )}
          </Paper>
        </Box>
      );
    }

    // Handle strings
    if (typeof value === 'string') {
      return (
        <Box key={currentPath} sx={{ ml: `${indent}px`, mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
          </Typography>
          <EditableText
            value={value}
            onSave={(newValue) => {
              if (parentPath) {
                const pathParts = parentPath.split('.');
                if (pathParts.length === 1) {
                  handleNestedFieldChange(pathParts[0], key, newValue);
                } else {
                  const parentObj = { ...data[pathParts[0]] };
                  let current = parentObj;
                  for (let i = 1; i < pathParts.length - 1; i++) {
                    current = current[pathParts[i]];
                  }
                  current[key] = newValue;
                  handleFieldChange(pathParts[0], parentObj);
                }
              } else {
                handleFieldChange(key, newValue);
              }
            }}
            multiline={value.length > 100}
            rows={value.length > 100 ? 3 : 1}
            placeholder={`Enter ${key.replace(/([A-Z])/g, ' $1').toLowerCase()}...`}
          />
        </Box>
      );
    }

    // Handle numbers
    if (typeof value === 'number') {
      return (
        <Box key={currentPath} sx={{ ml: `${indent}px`, mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
          </Typography>
          <TextField
            type="number"
            value={value}
            onChange={(e) => {
              const newValue = parseFloat(e.target.value) || 0;
              if (parentPath) {
                const pathParts = parentPath.split('.');
                if (pathParts.length === 1) {
                  handleNestedFieldChange(pathParts[0], key, newValue);
                } else {
                  handleFieldChange(key, newValue);
                }
              } else {
                handleFieldChange(key, newValue);
              }
            }}
            size="small"
            fullWidth
          />
        </Box>
      );
    }

    // Handle booleans
    if (typeof value === 'boolean') {
      return (
        <Box key={currentPath} sx={{ ml: `${indent}px`, mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={value}
                onChange={(e) => {
                  const newValue = e.target.checked;
                  if (parentPath) {
                    const pathParts = parentPath.split('.');
                    if (pathParts.length === 1) {
                      handleNestedFieldChange(pathParts[0], key, newValue);
                    } else {
                      handleFieldChange(key, newValue);
                    }
                  } else {
                    handleFieldChange(key, newValue);
                  }
                }}
              />
            }
            label={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
          />
        </Box>
      );
    }

    return null;
  };

  if (!data || Object.keys(data).length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No data available to edit.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {title && (
        <Typography variant="h6" fontWeight={600} color="primary.main" gutterBottom>
          {title}
        </Typography>
      )}
      {Object.entries(data).map(([key, value]) => renderComplexField(key, value))}
    </Box>
  );
};

const SubIndustryDetailsPage: React.FC = () => {
  const { industryId, subIndustryId } = useParams<{ industryId: string; subIndustryId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [industry, setIndustry] = useState<Industry | null>(null);
  const [subIndustry, setSubIndustry] = useState<SubIndustry | null>(null);
  const [isNewSubIndustry, setIsNewSubIndustry] = useState(false);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!industryId || !subIndustryId) {
        navigate('/admin/industries');
        return;
      }

      try {
        setLoading(true);

        // Fetch parent industry
        const foundIndustry = await industryApi.getIndustryById(industryId, {
          includeSubIndustries: true
        });
        setIndustry(foundIndustry);

        if (subIndustryId === 'new') {
          setIsNewSubIndustry(true);
          setSubIndustry({
            id: '',
            industryId: industryId,
            name: '',
            description: '',
            summary: '',
            isActive: true,
            growthRate: 0,
            marketSize: '',
            investmentOpportunities: 0,
            keyMetrics: { employment: '', exports: '', companies: 0 },
            growthData: []
          } as any);
        } else {
          // Fetch sub-industry from API
          const foundSubIndustry = await industryApi.getSubIndustryById(industryId, subIndustryId, {
            includeValueChain: true,
            includeSupportEcosystem: true
          });
          if (foundSubIndustry) {
            setSubIndustry(foundSubIndustry);
          } else {
            setSnackbar({
              open: true,
              message: 'Sub-industry not found',
              severity: 'error'
            });
            navigate(`/admin/industries/${industryId}`);
          }
        }
      } catch (error) {
        console.error('Error fetching sub-industry:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load sub-industry',
          severity: 'error'
        });
        navigate(`/admin/industries/${industryId}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [industryId, subIndustryId, navigate]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSave = async () => {
    if (!subIndustry || !industry || !industryId) return;

    if (!subIndustry.name || !subIndustry.description) {
      setSnackbar({
        open: true,
        message: 'Name and description are required fields',
        severity: 'error'
      });
      return;
    }

    try {
      if (isNewSubIndustry) {
        await industryApi.createSubIndustry(industryId, subIndustry as any);
        setSnackbar({
          open: true,
          message: 'Sub-industry created successfully',
          severity: 'success'
        });
        navigate(`/admin/industries/${industryId}`);
      } else {
        await industryApi.updateSubIndustry(industryId, subIndustry.id, subIndustry as any);
        setSnackbar({
          open: true,
          message: 'Sub-industry updated successfully',
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Error saving sub-industry:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save sub-industry',
        severity: 'error'
      });
    }
  };

  const handleInputChange = (field: keyof SubIndustry, value: any) => {
    if (!subIndustry) return;
    setSubIndustry(prev => prev ? { ...prev, [field]: value } : null);
  };

  const handleKeyMetricsChange = (field: keyof KeyMetrics, value: string | number) => {
    if (!subIndustry) return;
    setSubIndustry(prev => prev ? {
      ...prev,
      keyMetrics: { 
        ...prev.keyMetrics, 
        [field]: value || '' // Ensure the value is always a string
      } as KeyMetrics // Explicitly cast to KeyMetrics to satisfy the type
    } : null);
  };

  const getTabIcon = (index: number) => {
    switch (index) {
      case 0: return <DashboardIcon />;
      case 1: return <GavelIcon />;
      case 2: return <LightbulbIcon />;
      case 3: return <TrendingUpIcon />;
      case 4: return <CategoryIcon />;
      case 5: return <BusinessIcon />;
      case 6: return <Edit />;
      default: return undefined;
    }
  };

  if (loading || !industry || !subIndustry) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="h6" color="text.secondary">Loading sub-industry data...</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" color="primary" />}
        sx={{ mb: 4 }}
      >
        <Link
          color="inherit"
          onClick={() => navigate('/admin')}
          sx={{ cursor: 'pointer', textDecoration: 'none', '&:hover': { color: theme.palette.primary.main } }}
        >
          Admin
        </Link>
        <Link
          color="inherit"
          onClick={() => navigate('/admin/industries')}
          sx={{ cursor: 'pointer', textDecoration: 'none', '&:hover': { color: theme.palette.primary.main } }}
        >
          Industries
        </Link>
        <Link
          color="inherit"
          onClick={() => navigate(`/admin/industries/${industryId}`)}
          sx={{ cursor: 'pointer', textDecoration: 'none', '&:hover': { color: theme.palette.primary.main } }}
        >
          {industry.name}
        </Link>
        <Typography color="text.primary" fontWeight="medium">
          {isNewSubIndustry ? 'New Sub-Industry' : subIndustry.name}
        </Typography>
      </Breadcrumbs>

      {/* Hero Section */}
      <MotionPaper
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        elevation={0}
        sx={{
          mb: 4,
          p: { xs: 3, md: 4 },
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.9)}, ${alpha(theme.palette.primary.main, 0.8)})`,
          position: 'relative',
          overflow: 'hidden',
          color: 'white'
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <BusinessIcon sx={{ fontSize: 48 }} />
                <EditableText
                  value={subIndustry.name}
                  onSave={(value) => handleInputChange('name', value)}
                  variant="h3"
                  fontWeight={700}
                  color="white"
                  placeholder="Enter sub-industry name..."
                />
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
                Sub-industry of {industry.name}
              </Typography>
              <EditableText
                value={subIndustry.description || ''}
                onSave={(value) => handleInputChange('description', value)}
                variant="body1"
                color="white"
                multiline
                rows={2}
                placeholder="Enter sub-industry description..."
                sx={{ opacity: 0.8, maxWidth: 600 }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate(`/admin/industries/${industryId}`)}
                startIcon={<ArrowBackIcon />}
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  '&:hover': { borderColor: 'white', bgcolor: alpha('#fff', 0.1) },
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Back to Industry
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                startIcon={<SaveIcon />}
                sx={{
                  bgcolor: 'white',
                  color: theme.palette.primary.main,
                  '&:hover': { bgcolor: alpha('#fff', 0.9) },
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                {isNewSubIndustry ? 'Create Sub-Industry' : 'Save Changes'}
              </Button>
            </Box>
          </Box>
        </Box>
      </MotionPaper>

      {/* Market Summary Section */}
      {subIndustry.summary && (
        <Paper
          elevation={2}
          sx={{
            mb: 6,
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            background: `linear-gradient(to right, ${alpha(theme.palette.secondary.main, 0.05)}, ${alpha(theme.palette.background.paper, 1)} 70%)`,
            border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`
          }}
        >
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              fontWeight: 600,
              mb: 2,
              position: 'relative',
              display: 'inline-block',
              '&:after': {
                content: '""',
                position: 'absolute',
                bottom: -8,
                left: 0,
                width: '50%',
                height: 4,
                borderRadius: 2,
                backgroundColor: theme.palette.secondary.main
              }
            }}
          >
            Market Summary
          </Typography>

          <EditableText
            value={subIndustry.summary || ''}
            onSave={(value) => handleInputChange('summary', value)}
            variant="body1"
            multiline
            rows={6}
            placeholder="Enter market summary..."
            sx={{
              mt: 3,
              lineHeight: 1.8,
              color: theme.palette.text.primary
            }}
          />
        </Paper>
      )}

      {/* Main Content Tabs */}
      <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: `1px solid ${theme.palette.divider}`,
            '& .MuiTab-root': {
              minHeight: 64,
              px: 3,
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '1rem'
            }
          }}
        >
          <Tab label="Market Overview" icon={getTabIcon(0)} iconPosition="start" />
          <Tab label="Legal & Infrastructure" icon={getTabIcon(1)} iconPosition="start" />
          <Tab label="Opportunities & Incentives" icon={getTabIcon(2)} iconPosition="start" />
          <Tab label="Trends" icon={getTabIcon(3)} iconPosition="start" />
          <Tab label="Value Chain" icon={getTabIcon(4)} iconPosition="start" />
          <Tab label="Support Ecosystem" icon={getTabIcon(5)} iconPosition="start" />
        </Tabs>

        <Box sx={{ p: 4 }}>
          {/* Market Overview Tab */}
          {tabValue === 0 && (
            <Box>
              <Typography variant="h5" gutterBottom fontWeight={600}>
                Market Overview
              </Typography>

              {subIndustry.tabs?.marketOverview ? (
                <EditableObject
                  data={subIndustry.tabs.marketOverview}
                  onUpdate={(newData) => {
                    const updatedTabs = { ...subIndustry.tabs, marketOverview: newData };
                    handleInputChange('tabs', updatedTabs);
                  }}
                />
              ) : (
                <Paper
                  elevation={1}
                  sx={{
                    mt: 3,
                    p: 3,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.warning.light, 0.1),
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`
                  }}
                >
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    No market overview data available. Click to add initial data.
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const initialData = {
                        marketPotential: [''],
                        criticalGaps: [''],
                        criticalStrengths: [''],
                        gapsBlockingGrowth: [''],
                        investmentShortfalls: [''],
                        bottomLine: {
                          text: '',
                          points: ['']
                        }
                      };
                      const updatedTabs = { ...subIndustry.tabs, marketOverview: initialData };
                      handleInputChange('tabs', updatedTabs);
                    }}
                    sx={{ mt: 2 }}
                  >
                    Add Market Overview Data
                  </Button>
                </Paper>
              )}
            </Box>
          )}

          {/* Legal & Infrastructure Tab */}
          {tabValue === 1 && (
            <Box>
              <Typography variant="h5" gutterBottom fontWeight={600}>
                Legal & Infrastructure
              </Typography>

              {subIndustry.tabs?.legalInfrastructure ? (
                <EditableObject
                  data={subIndustry.tabs.legalInfrastructure}
                  onUpdate={(newData) => {
                    const updatedTabs = { ...subIndustry.tabs, legalInfrastructure: newData };
                    handleInputChange('tabs', updatedTabs);
                  }}
                />
              ) : (
                <Paper
                  elevation={1}
                  sx={{
                    mt: 3,
                    p: 3,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.warning.light, 0.1),
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`
                  }}
                >
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    No legal & infrastructure data available.
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const initialData = {
                        legal: {
                          regulatoryLinks: [],
                          notes: [''],
                          foundations: ['']
                        },
                        infrastructure: {
                          physical: [''],
                          digital: [''],
                          priorities: {
                            intro: '',
                            items: ['']
                          }
                        }
                      };
                      const updatedTabs = { ...subIndustry.tabs, legalInfrastructure: initialData };
                      handleInputChange('tabs', updatedTabs);
                    }}
                    sx={{ mt: 2 }}
                  >
                    Add Legal & Infrastructure Data
                  </Button>
                </Paper>
              )}
            </Box>
          )}

          {/* Opportunities & Incentives Tab */}
          {tabValue === 2 && (
            <Box>
              <Typography variant="h5" gutterBottom fontWeight={600}>
                Opportunities & Incentives
              </Typography>

              {subIndustry.tabs?.incentives ? (
                <EditableObject
                  data={subIndustry.tabs.incentives}
                  onUpdate={(newData) => {
                    const updatedTabs = { ...subIndustry.tabs, incentives: newData };
                    handleInputChange('tabs', updatedTabs);
                  }}
                />
              ) : (
                <Paper
                  elevation={1}
                  sx={{
                    mt: 3,
                    p: 3,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.warning.light, 0.1),
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`
                  }}
                >
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    No incentives data available.
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const initialData = {
                        title: 'Incentives',
                        content: {
                          title: 'Investment Incentives',
                          sections: [
                            {
                              title: 'Financial Incentives',
                              points: ['']
                            }
                          ]
                        }
                      };
                      const updatedTabs = { ...subIndustry.tabs, incentives: initialData };
                      handleInputChange('tabs', updatedTabs);
                    }}
                    sx={{ mt: 2 }}
                  >
                    Add Incentives Data
                  </Button>
                </Paper>
              )}
            </Box>
          )}

          {/* Trends Tab */}
          {tabValue === 3 && (
            <Box>
              <Typography variant="h5" gutterBottom fontWeight={600}>
                Trends
              </Typography>

              {subIndustry.tabs?.trends ? (
                <EditableObject
                  data={subIndustry.tabs.trends}
                  onUpdate={(newData) => {
                    const updatedTabs = { ...subIndustry.tabs, trends: newData };
                    handleInputChange('tabs', updatedTabs);
                  }}
                />
              ) : (
                <Paper
                  elevation={1}
                  sx={{
                    mt: 3,
                    p: 3,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.warning.light, 0.1),
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`
                  }}
                >
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    No trends data available.
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const initialData = {
                        trendsList: [
                          {
                            name: '',
                            points: ['']
                          }
                        ],
                        whereToStart: ['']
                      };
                      const updatedTabs = { ...subIndustry.tabs, trends: initialData };
                      handleInputChange('tabs', updatedTabs);
                    }}
                    sx={{ mt: 2 }}
                  >
                    Add Trends Data
                  </Button>
                </Paper>
              )}
            </Box>
          )}

          {/* Value Chain Tab */}
          {tabValue === 4 && (
            <Box>
              <Typography variant="h5" gutterBottom fontWeight={600}>
                Value Chain
              </Typography>

              <Paper
                elevation={1}
                sx={{
                  mt: 2,
                  borderRadius: 3,
                  overflow: 'hidden'
                }}
              >
                <Box
                  sx={{
                    p: 3,
                    background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
                    color: 'white'
                  }}
                >
                  <Typography variant="h6" fontWeight={600}>
                    Sub-Industry Value Chain Analysis
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                    Detailed breakdown of the value chain stages and components
                  </Typography>
                </Box>

                <Box p={3}>
                  {(subIndustry.subIndustries && subIndustry.subIndustries.length > 0) || (subIndustry.valueChainStages && subIndustry.valueChainStages.length > 0) ? (
                    <EditableValueChainTable
                      valueChainId={subIndustry.id}
                      valueChainData={(() => {
                        // Get child sub-industries - they take priority
                        const childSubInds = subIndustry.subIndustries && subIndustry.subIndustries.length > 0
                          ? subIndustry.subIndustries.filter((sub: SubIndustry) => sub.parentSubIndustryId === subIndustry.id)
                          : [];

                        // If we have child sub-industries, create default stages with sub-industries as rows
                        if (childSubInds.length > 0) {
                          // Create a row (category) for each sub-industry
                          const categories: { [key: string]: string } = {};
                          childSubInds.forEach((sub: SubIndustry) => {
                            categories[sub.name] = '';
                          });

                          const stages = [
                            { name: 'Import', content: '', categories: { ...categories }, order: 0 },
                            { name: 'Research & Discovery', content: '', categories: { ...categories }, order: 1 },
                            { name: 'Development', content: '', categories: { ...categories }, order: 2 },
                            { name: 'Manufacturing', content: '', categories: { ...categories }, order: 3 },
                            { name: 'Commercialization', content: '', categories: { ...categories }, order: 4 },
                            { name: 'Export', content: '', categories: { ...categories }, order: 5 }
                          ];

                          return { stages, additionalContent: [] };
                        }

                        // Check if there's already saved value chain data (from manual edits)
                        if (subIndustry?.valueChain && subIndustry.valueChain.stages && subIndustry.valueChain.stages.length > 0) {
                          return subIndustry.valueChain;
                        }

                        // Check if we have value chain stages from the database
                        if (subIndustry.valueChainStages && subIndustry.valueChainStages.length > 0) {
                          return {
                            stages: subIndustry.valueChainStages.sort((a: any, b: any) => (a.order || 0) - (b.order || 0)),
                            additionalContent: []
                          };
                        }

                        // Default empty structure
                        return {
                          stages: [],
                          additionalContent: []
                        };
                      })()}
                      industryId={industryId}
                      subIndustryId={subIndustryId}
                      onNavigate={(stageName: string, stageId?: string) => {
                        // Navigate to sub-sub-industry edit page when clicking on value chain item
                        if (stageId) {
                          navigate(`/admin/sub-industry-details/${industryId}/${stageId}`);
                        }
                      }}
                      onUpdate={(updatedValueChain: any) => {
                        handleInputChange('valueChain', updatedValueChain);
                      }}
                    />
                  ) : (
                    <Paper
                      elevation={1}
                      sx={{
                        p: 4,
                        borderRadius: 2,
                        background: alpha(theme.palette.warning.light, 0.1),
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`
                      }}
                    >
                      <Typography variant="h6" color="warning.dark" fontWeight={600} gutterBottom>
                        Value Chain Data Not Available
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                        Value chain data for this sub-industry is currently being developed.
                      </Typography>
                      <Button
                        variant="outlined"
                        color="warning"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          const initialValueChain = {
                            stages: [
                              {
                                name: 'Import',
                                content: '',
                                categories: {}
                              },
                              {
                                name: 'Research & Discovery',
                                content: '',
                                categories: {}
                              },
                              {
                                name: 'Development',
                                content: '',
                                categories: {}
                              },
                              {
                                name: 'Manufacturing',
                                content: '',
                                categories: {}
                              },
                              {
                                name: 'Commercialization',
                                content: '',
                                categories: {}
                              }
                            ],
                            additionalContent: []
                          };
                          handleInputChange('valueChain', initialValueChain);
                        }}
                        sx={{ borderRadius: 2, textTransform: 'none' }}
                      >
                        Add Value Chain Data
                      </Button>
                    </Paper>
                  )}
                </Box>
              </Paper>
            </Box>
          )}

          {/* Support Ecosystem Tab */}
          {tabValue === 5 && (
            <Box>
              <Typography variant="h5" gutterBottom fontWeight={600}>
                Support Ecosystem
              </Typography>

              {subIndustry.supportEcosystem ? (
                <EditableObject
                  data={subIndustry.supportEcosystem}
                  onUpdate={(newData) => handleInputChange('supportEcosystem', newData)}
                />
              ) : (
                <Paper
                  elevation={1}
                  sx={{
                    mt: 3,
                    p: 3,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.warning.light, 0.1),
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`
                  }}
                >
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    No support ecosystem data available.
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const initialData = {
                        summary: '',
                        productsServicesNeeded: [''],
                        keyPlayers: [''],
                        trends: '',
                        challenges: '',
                        riskRating: '',
                        policyRecommendations: [''],
                        talent: {
                          currentWorkforceGaps: {
                            criticalShortages: ['']
                          }
                        },
                        funding: {
                          investmentLandscape: {
                            keyMetrics: ['']
                          }
                        },
                        supplyChain: {
                          criticalGaps: ['']
                        }
                      };
                      handleInputChange('supportEcosystem', initialData);
                    }}
                    sx={{ mt: 2 }}
                  >
                    Add Support Ecosystem Data
                  </Button>
                </Paper>
              )}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Floating Save Button */}
      <Fab
        color="secondary"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1000
        }}
        onClick={handleSave}
      >
        <SaveIcon />
      </Fab>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SubIndustryDetailsPage;